import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCareer } from "../../../features/career/careerSlice";
import { careerForm, resetCareerForm } from "../../../features/career_form/careerformSlice";
import "./Career.css";

const MAX_MB = 5;
const OK_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const OK_EXT = /\.(pdf|docx?)$/i;

const QUALIFICATIONS = ["10th / SSC", "12th / HSC", "Diploma", "Bachelor's degree", "Master's degree", "Optometry diploma / degree", "Other"];
const EXPERIENCE = ["Fresher", "Less than 1 year", "1-2 years", "2-5 years", "5-10 years", "10+ years"];
const NOTICE = ["Immediately", "Within 15 days", "1 month", "2 months", "3 months or more"];
const STEPS = ["About you", "Experience", "Resume"];

const EMPTY = {
  name: "", email: "", phone: "", city: "",
  qualification: "", experience: "", current_company: "", current_designation: "",
  current_ctc: "", expected_ctc: "", notice_period: "", skills: "", linkedin: "",
  cover_note: "", consent: false,
};

const money = (n) => (n || n === 0 ? Number(n).toLocaleString("en-IN") : "");
const isClosed = (d) => !!d && new Date(d).setHours(23, 59, 59, 999) < Date.now();
const daysLeft = (d) => (d ? Math.ceil((new Date(d).setHours(23, 59, 59, 999) - Date.now()) / 86400000) : null);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const names = (rows, key) => (rows || []).map((r) => r?.[key]?.name).filter(Boolean);
const initials = (s = "") => s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "B";
const splitSkills = (s) => (s ? String(s).split(/[,\n;|]/).map((x) => x.trim()).filter(Boolean) : []);
const pkg = (j) =>
  j.start_annual_package ? `₹${money(j.start_annual_package)}${j.end_annual_package ? ` – ${money(j.end_annual_package)}` : ""} / yr` : "";
const exp = (j) => (j.experience_from != null && j.experience_to != null ? `${j.experience_from}-${j.experience_to} yrs` : "");
const sizeLabel = (b) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`);

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const I = {
  pin: "M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z",
  clock: "M12 6v6l4 2M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z",
  cash: "M3 7h18v10H3zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  user: "M20 21a8 8 0 0 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  search: "M21 21l-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z",
  upload: "M12 16V4m0 0L7 9m5-5l5 5M4 20h16",
  check: "M5 13l4 4L19 7",
  x: "M6 6l12 12M18 6L6 18",
  file: "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5",
};

const Tag = ({ icon, children }) =>
  children ? (
    <span className="cr-tag">
      {icon && <Icon d={icon} size={14} />}
      {children}
    </span>
  ) : null;

const Fact = ({ label, value }) =>
  value ? (
    <div className="cr-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  ) : null;

const Field = ({ label, error, hint, required, children, wide }) => (
  <label className={`cr-field${wide ? " is-wide" : ""}${error ? " has-error" : ""}`}>
    <span className="cr-label">
      {label}
      {required && <em aria-hidden="true"> *</em>}
    </span>
    {children}
    {hint && !error && <small className="cr-hint">{hint}</small>}
    {error && <span className="cr-err" role="alert">{error}</span>}
  </label>
);

const Career = () => {
  const dispatch = useDispatch();
  const { addCareer, status, error } = useSelector((s) => s.career);
  const apply = useSelector((s) => s.careerform);

  const [term, setTerm] = useState("");
  const [type, setType] = useState("");
  const [place, setPlace] = useState("");
  const [sort, setSort] = useState("new");
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("role"); // small screens: role | apply
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [errs, setErrs] = useState({});
  const dialogRef = useRef(null);

  useEffect(() => {
    dispatch(getCareer());
  }, [dispatch]);

  const jobs = useMemo(() => (Array.isArray(addCareer) ? addCareer : addCareer?.data || []), [addCareer]);
  const types = useMemo(() => {
    const m = new Map();
    jobs.forEach((j) => j?.JobType?.name && m.set(j.JobType.name, (m.get(j.JobType.name) || 0) + 1));
    return [...m.entries()];
  }, [jobs]);
  const places = useMemo(() => [...new Set(jobs.map((j) => j.job_location).filter(Boolean))].sort(), [jobs]);

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase();
    const list = jobs.filter((j) => {
      const hay = `${j.name} ${j.skill} ${j.job_location} ${j.company_name}`.toLowerCase();
      return (!t || hay.includes(t)) && (!type || j?.JobType?.name === type) && (!place || j.job_location === place);
    });
    const open1 = (j) => (isClosed(j.deadline) ? 1 : 0);
    return [...list].sort((a, b) => {
      if (open1(a) !== open1(b)) return open1(a) - open1(b); // closed roles last
      if (sort === "deadline") return new Date(a.deadline || 8.64e15) - new Date(b.deadline || 8.64e15);
      if (sort === "pay") return Number(b.end_annual_package || b.start_annual_package || 0) - Number(a.end_annual_package || a.start_annual_package || 0);
      return Number(b.id) - Number(a.id);
    });
  }, [jobs, term, type, place, sort]);

  const filtered = !!(term || type || place);
  const clear = () => { setTerm(""); setType(""); setPlace(""); };
  const openRoles = jobs.filter((j) => !isClosed(j.deadline)).length;

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errs[k]) setErrs((x) => ({ ...x, [k]: undefined }));
  };

  const close = useCallback(() => {
    setOpen(null);
    setTab("role");
    setStep(0);
    setForm(EMPTY);
    setFile(null);
    setErrs({});
    dispatch(resetCareerForm());
  }, [dispatch]);

  const show = (j) => {
    setOpen(j);
    setTab("role");
    setStep(0);
    dispatch(resetCareerForm());
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setErrs((x) => ({ ...x, resume: undefined }));
  };

  // returns the errors of one step
  const check = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.name.trim()) e.name = "Enter your full name";
      if (!form.email.trim()) e.email = "Enter your email";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Enter a valid email";
      const digits = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
      if (!form.phone.trim()) e.phone = "Enter your mobile number";
      else if (!/^[6-9]\d{9}$/.test(digits)) e.phone = "Enter a valid 10 digit mobile number";
    }
    if (s === 1) {
      if (!form.qualification) e.qualification = "Select your highest qualification";
      if (!form.experience) e.experience = "Select your experience";
      if (form.linkedin.trim() && !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i.test(form.linkedin.trim()))
        e.linkedin = "Enter a valid link";
      ["current_ctc", "expected_ctc"].forEach((k) => {
        if (form[k] !== "" && (!/^\d+(\.\d+)?$/.test(form[k]) || Number(form[k]) < 0)) e[k] = "Numbers only";
      });
    }
    if (s === 2) {
      if (!file) e.resume = "Attach your resume";
      else if (!OK_TYPES.includes(file.type) && !OK_EXT.test(file.name)) e.resume = "Resume must be a PDF or Word file";
      else if (file.size > MAX_MB * 1024 * 1024) e.resume = `Resume must be under ${MAX_MB} MB`;
      if (!form.consent) e.consent = "Please confirm to continue";
    }
    return e;
  };

  const next = () => {
    const e = check(step);
    setErrs(e);
    if (!Object.keys(e).length) setStep((s) => s + 1);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (step < 2) return next();
    const all = { ...check(0), ...check(1), ...check(2) };
    setErrs(all);
    if (Object.keys(all).length) {
      setStep(Object.keys(check(0)).length ? 0 : Object.keys(check(1)).length ? 1 : 2);
      return;
    }
    const fd = new FormData();
    fd.append("career_id", open.id);
    fd.append("resume", file);
    Object.entries(form).forEach(([k, v]) => {
      if (k === "consent") return;
      const val = typeof v === "string" ? v.trim() : v;
      if (val !== "") fd.append(k, val);
    });
    try {
      await dispatch(careerForm(fd)).unwrap();
    } catch (_) {
      /* the message is in apply.error and shown in the form */
    }
  };

  const sent = apply.status === "succeeded";
  const sending = apply.status === "loading";
  const closed = open ? isClosed(open.deadline) : false;

  return (
    <main className="cr-page">
      {/* ------------------------------------------------------------ hero */}
      <header className="cr-hero">
        <div className="cr-hero__in">
          <span className="cr-eyebrow">Careers</span>
          <h1>Build a career in eye care</h1>
          <p>
            We fit glasses, test eyes and look after families who have trusted Bapat Optics for generations. Find the
            role that suits you and apply in a few minutes.
          </p>
          <ul className="cr-stats" aria-label="At a glance">
            <li><strong>{status === "succeeded" ? openRoles : "–"}</strong><span>open {openRoles === 1 ? "role" : "roles"}</span></li>
            <li><strong>{places.length || "–"}</strong><span>{places.length === 1 ? "location" : "locations"}</span></li>
            <li><strong>{types.length || "–"}</strong><span>job {types.length === 1 ? "type" : "types"}</span></li>
          </ul>
        </div>
      </header>

      {/* ------------------------------------------------------------ filters */}
      <section className="cr-tools" aria-label="Find a job">
        <div className="cr-search">
          <Icon d={I.search} size={18} />
          <input type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search by role, skill or city" aria-label="Search jobs" />
        </div>
        <select value={place} onChange={(e) => setPlace(e.target.value)} aria-label="Location">
          <option value="">All locations</option>
          {places.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort roles">
          <option value="new">Newest first</option>
          <option value="deadline">Closing soon</option>
          <option value="pay">Highest package</option>
        </select>
      </section>

      {types.length > 0 && (
        <div className="cr-chips" role="group" aria-label="Job type">
          <button type="button" className={`cr-chip${!type ? " is-on" : ""}`} onClick={() => setType("")} aria-pressed={!type}>
            All <span>{jobs.length}</span>
          </button>
          {types.map(([t, n]) => (
            <button type="button" key={t} className={`cr-chip${type === t ? " is-on" : ""}`} onClick={() => setType(type === t ? "" : t)} aria-pressed={type === t}>
              {t} <span>{n}</span>
            </button>
          ))}
        </div>
      )}

      <div className="cr-count" aria-live="polite">
        {status === "succeeded" && (
          <>
            Showing {shown.length} of {jobs.length} {jobs.length === 1 ? "role" : "roles"}
            {filtered && <button type="button" className="cr-link" onClick={clear}>Clear filters</button>}
          </>
        )}
      </div>

      {status === "loading" && (
        <ul className="cr-grid" aria-label="Loading open positions">
          {[0, 1, 2, 3].map((i) => <li key={i} className="cr-card cr-skel" aria-hidden="true" />)}
        </ul>
      )}
      {status === "failed" && <p className="cr-note cr-bad">We couldn't load the jobs. {error} Refresh the page to try again.</p>}
      {status === "succeeded" && !shown.length && (
        <div className="cr-empty">
          <strong>{jobs.length ? "No roles match your search" : "No openings right now"}</strong>
          <p>{jobs.length ? "Try a different keyword or clear the filters." : "Check back soon, new roles are added often."}</p>
          {filtered && <button type="button" className="cr-btn cr-btn--ghost" onClick={clear}>Clear filters</button>}
        </div>
      )}

      {/* ------------------------------------------------------------ jobs */}
      <ul className="cr-grid">
        {shown.map((j) => {
          const closedJ = isClosed(j.deadline);
          const left = daysLeft(j.deadline);
          const skills = splitSkills(j.skill);
          return (
            <li key={j.id} className={`cr-card${closedJ ? " is-closed" : ""}`}>
              <div className="cr-card__top">
                <span className="cr-logo" aria-hidden="true">{initials(j.company_name || "Bapat Optics")}</span>
                <div>
                  <h2>{j.name}</h2>
                  <span className="cr-where">{j.company_name || "Bapat Optics"}</span>
                </div>
                {closedJ ? (
                  <span className="cr-badge cr-badge--off">Closed</span>
                ) : left != null && left <= 7 ? (
                  <span className="cr-badge cr-badge--hot">{left <= 0 ? "Last day" : `${left} ${left === 1 ? "day" : "days"} left`}</span>
                ) : null}
              </div>

              <div className="cr-tags">
                <Tag icon={I.pin}>{j.job_location}</Tag>
                <Tag icon={I.clock}>{j?.JobType?.name}</Tag>
                <Tag>{j?.Shift?.name}</Tag>
                <Tag icon={I.user}>{exp(j)}</Tag>
                <Tag icon={I.cash}>{pkg(j)}</Tag>
              </div>

              {skills.length > 0 && (
                <ul className="cr-skills" aria-label="Skills">
                  {skills.slice(0, 5).map((s) => <li key={s}>{s}</li>)}
                  {skills.length > 5 && <li className="is-more">+{skills.length - 5}</li>}
                </ul>
              )}

              <div className="cr-card__foot">
                <span className="cr-small">
                  {j.vacancy ? `${j.vacancy} ${Number(j.vacancy) === 1 ? "opening" : "openings"}` : ""}
                  {j.vacancy && j.deadline ? " · " : ""}
                  {j.deadline ? `${closedJ ? "Closed" : "Apply by"} ${fmtDate(j.deadline)}` : ""}
                </span>
                <button type="button" className="cr-btn" onClick={() => show(j)}>
                  {closedJ ? "View role" : "View & apply"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* ------------------------------------------------------------ dialog */}
      {open && (
        <div className="cr-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
          <div className="cr-dialog" role="dialog" aria-modal="true" aria-labelledby="cr-title" tabIndex={-1} ref={dialogRef}>
            <button type="button" className="cr-x" onClick={close} aria-label="Close"><Icon d={I.x} size={20} /></button>

            <div className="cr-tabs" role="tablist" aria-label="Role or application">
              <button type="button" role="tab" aria-selected={tab === "role"} className={tab === "role" ? "is-on" : ""} onClick={() => setTab("role")}>Role details</button>
              <button type="button" role="tab" aria-selected={tab === "apply"} className={tab === "apply" ? "is-on" : ""} onClick={() => setTab("apply")}>{closed ? "Status" : "Apply"}</button>
            </div>

            {/* left: the role */}
            <section className={`cr-pane cr-pane--role${tab === "role" ? " is-active" : ""}`}>
              <div className="cr-card__top">
                <span className="cr-logo" aria-hidden="true">{initials(open.company_name || "Bapat Optics")}</span>
                <div>
                  <h2 id="cr-title">{open.name}</h2>
                  <span className="cr-where">{[open.company_name, open.job_location].filter(Boolean).join(" · ")}</span>
                </div>
              </div>

              <dl className="cr-facts">
                <Fact label="Job type" value={open?.JobType?.name} />
                <Fact label="Shift" value={open?.Shift?.name} />
                <Fact label="Experience" value={exp(open)} />
                <Fact label="Package" value={pkg(open)} />
                <Fact label="Openings" value={open.vacancy} />
                <Fact label="Apply by" value={fmtDate(open.deadline)} />
              </dl>

              {open.description && (
                <>
                  <h3>About the role</h3>
                  <div className="cr-rich" dangerouslySetInnerHTML={{ __html: open.description }} />
                </>
              )}
              {open.role_permission && (
                <>
                  <h3>Role and responsibilities</h3>
                  <div className="cr-rich" dangerouslySetInnerHTML={{ __html: open.role_permission }} />
                </>
              )}
              {splitSkills(open.skill).length > 0 && (
                <>
                  <h3>Skills</h3>
                  <ul className="cr-skills">{splitSkills(open.skill).map((s) => <li key={s}>{s}</li>)}</ul>
                </>
              )}
              {names(open.Career_Qualifications, "Qualification").length > 0 && (
                <>
                  <h3>Qualification</h3>
                  <ul className="cr-skills">{names(open.Career_Qualifications, "Qualification").map((s) => <li key={s}>{s}</li>)}</ul>
                </>
              )}
              {names(open.Career_languages, "Language").length > 0 && (
                <>
                  <h3>Languages</h3>
                  <ul className="cr-skills">{names(open.Career_languages, "Language").map((s) => <li key={s}>{s}</li>)}</ul>
                </>
              )}
            </section>

            {/* right: the application */}
            <section className={`cr-pane cr-pane--apply${tab === "apply" ? " is-active" : ""}`}>
              {closed ? (
                <p className="cr-closed">Applications for this role closed on {fmtDate(open.deadline)}. Browse the other open roles on this page.</p>
              ) : sent ? (
                <div className="cr-done" role="status">
                  <span className="cr-done__tick"><Icon d={I.check} size={30} /></span>
                  <h3>Application sent</h3>
                  <p>{apply.message || "Thank you for applying."} We'll contact you on {form.email || "your email"} if your profile fits.</p>
                  <button type="button" className="cr-btn" onClick={close}>Back to jobs</button>
                </div>
              ) : (
                <form className="cr-form" onSubmit={submit} noValidate>
                  <h3 className="cr-form__title">Apply for this role</h3>
                  <ol className="cr-steps" aria-label="Progress">
                    {STEPS.map((s, i) => (
                      <li key={s} className={i === step ? "is-now" : i < step ? "is-done" : ""} aria-current={i === step ? "step" : undefined}>
                        <span>{i < step ? <Icon d={I.check} size={14} /> : i + 1}</span>
                        {s}
                      </li>
                    ))}
                  </ol>

                  {step === 0 && (
                    <div className="cr-fields">
                      <Field label="Full name" required error={errs.name} wide>
                        <input value={form.name} onChange={set("name")} maxLength={100} autoComplete="name" aria-invalid={!!errs.name} />
                      </Field>
                      <Field label="Email" required error={errs.email}>
                        <input type="email" value={form.email} onChange={set("email")} maxLength={120} autoComplete="email" aria-invalid={!!errs.email} />
                      </Field>
                      <Field label="Mobile number" required error={errs.phone}>
                        <input type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} maxLength={16} autoComplete="tel" placeholder="10 digit number" aria-invalid={!!errs.phone} />
                      </Field>
                      <Field label="Current city" error={errs.city} wide>
                        <input value={form.city} onChange={set("city")} maxLength={80} autoComplete="address-level2" />
                      </Field>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="cr-fields">
                      <Field label="Highest qualification" required error={errs.qualification}>
                        <select value={form.qualification} onChange={set("qualification")} aria-invalid={!!errs.qualification}>
                          <option value="">Select</option>
                          {QUALIFICATIONS.map((q) => <option key={q}>{q}</option>)}
                        </select>
                      </Field>
                      <Field label="Total experience" required error={errs.experience}>
                        <select value={form.experience} onChange={set("experience")} aria-invalid={!!errs.experience}>
                          <option value="">Select</option>
                          {EXPERIENCE.map((q) => <option key={q}>{q}</option>)}
                        </select>
                      </Field>
                      <Field label="Current company" error={errs.current_company}>
                        <input value={form.current_company} onChange={set("current_company")} maxLength={120} />
                      </Field>
                      <Field label="Current designation" error={errs.current_designation}>
                        <input value={form.current_designation} onChange={set("current_designation")} maxLength={120} />
                      </Field>
                      <Field label="Current salary (₹ per year)" error={errs.current_ctc}>
                        <input inputMode="numeric" value={form.current_ctc} onChange={set("current_ctc")} maxLength={10} aria-invalid={!!errs.current_ctc} />
                      </Field>
                      <Field label="Expected salary (₹ per year)" error={errs.expected_ctc}>
                        <input inputMode="numeric" value={form.expected_ctc} onChange={set("expected_ctc")} maxLength={10} aria-invalid={!!errs.expected_ctc} />
                      </Field>
                      <Field label="Notice period" error={errs.notice_period}>
                        <select value={form.notice_period} onChange={set("notice_period")}>
                          <option value="">Select</option>
                          {NOTICE.map((q) => <option key={q}>{q}</option>)}
                        </select>
                      </Field>
                      <Field label="LinkedIn or portfolio" error={errs.linkedin} hint="Optional">
                        <input type="url" value={form.linkedin} onChange={set("linkedin")} maxLength={200} placeholder="https://" aria-invalid={!!errs.linkedin} />
                      </Field>
                      <Field label="Key skills" wide hint="Separate with commas, for example: refraction, frame fitting, billing">
                        <input value={form.skills} onChange={set("skills")} maxLength={250} />
                      </Field>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="cr-fields">
                      <div className="cr-field is-wide">
                        <span className="cr-label">Resume<em aria-hidden="true"> *</em></span>
                        <label
                          className={`cr-drop${drag ? " is-drag" : ""}${errs.resume ? " has-error" : ""}${file ? " has-file" : ""}`}
                          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                          onDragLeave={() => setDrag(false)}
                          onDrop={(e) => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files?.[0]); }}
                        >
                          <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => pickFile(e.target.files?.[0])} aria-invalid={!!errs.resume} />
                          {file ? (
                            <>
                              <Icon d={I.file} size={26} />
                              <strong>{file.name}</strong>
                              <small>{sizeLabel(file.size)} · click to replace</small>
                            </>
                          ) : (
                            <>
                              <Icon d={I.upload} size={26} />
                              <strong>Drop your resume here or browse</strong>
                              <small>PDF or Word, up to {MAX_MB} MB</small>
                            </>
                          )}
                        </label>
                        {errs.resume && <span className="cr-err" role="alert">{errs.resume}</span>}
                      </div>

                      <Field label="Why should we hire you?" wide hint={`${form.cover_note.length}/1000`}>
                        <textarea rows={4} maxLength={1000} value={form.cover_note} onChange={set("cover_note")} placeholder="A few lines about you and why this role (optional)" />
                      </Field>

                      <label className={`cr-check is-wide${errs.consent ? " has-error" : ""}`}>
                        <input type="checkbox" checked={form.consent} onChange={set("consent")} aria-invalid={!!errs.consent} />
                        <span>I confirm the details are correct and agree that Bapat Optics may contact me about this application.</span>
                      </label>
                      {errs.consent && <span className="cr-err is-wide" role="alert">{errs.consent}</span>}
                    </div>
                  )}

                  {apply.status === "failed" && <p className="cr-alert" role="alert">{apply.error || "We couldn't send your application. Please try again."}</p>}

                  <div className="cr-actions">
                    {step > 0 ? (
                      <button type="button" className="cr-btn cr-btn--ghost" onClick={() => setStep((s) => s - 1)} disabled={sending}>Back</button>
                    ) : <span />}
                    {step < 2 ? (
                      <button type="button" className="cr-btn" onClick={next}>Continue</button>
                    ) : (
                      <button type="submit" className="cr-btn" disabled={sending}>{sending ? "Sending…" : "Send application"}</button>
                    )}
                  </div>
                </form>
              )}
            </section>
          </div>
        </div>
      )}
    </main>
  );
};

export default Career;